import { Link } from 'react-router-dom';
import './CustomerTable.css';

function CustomerTable({ customers, sortBy, sortOrder, onSort }) {
  const handleSort = (field) => {
    if (sortBy === field) {
      onSort(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(field, 'desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="sort-icon">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="sort-icon active">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="sort-icon active">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'badge-success' },
      inactive: { label: 'Inactive', className: 'badge-secondary' },
      blocked: { label: 'Blocked', className: 'badge-danger' },
    };
    const config = statusConfig[status] || statusConfig.active;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (customers.length === 0) {
    return (
      <div className="table-empty">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
        <h3>No customers found</h3>
        <p>Try adjusting your search or filter criteria.</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="customer-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort('name')}>
              Customer {getSortIcon('name')}
            </th>
            <th>Status</th>
            <th className="sortable text-center" onClick={() => handleSort('totalOrders')}>
              Orders {getSortIcon('totalOrders')}
            </th>
            <th className="sortable text-right" onClick={() => handleSort('totalSpent')}>
              Total Spent {getSortIcon('totalSpent')}
            </th>
            <th className="sortable" onClick={() => handleSort('lastOrderDate')}>
              Last Order {getSortIcon('lastOrderDate')}
            </th>
            <th>Tags</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr key={customer.id}>
              <td>
                <div className="customer-info">
                  <div className="customer-avatar">
                    {getInitials(customer.firstName, customer.lastName)}
                  </div>
                  <div className="customer-details">
                    <Link to={`/customers/${customer.id}`} className="customer-name">
                      {customer.firstName} {customer.lastName}
                    </Link>
                    <span className="customer-email">{customer.email}</span>
                  </div>
                </div>
              </td>
              <td>{getStatusBadge(customer.status)}</td>
              <td className="text-center orders-cell">{customer.totalOrders}</td>
              <td className="text-right price-cell">{formatPrice(customer.totalSpent)}</td>
              <td className="date-cell">{formatDate(customer.lastOrderDate)}</td>
              <td>
                <div className="tags-cell">
                  {customer.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                  {customer.tags.length > 2 && (
                    <span className="tag tag-more">+{customer.tags.length - 2}</span>
                  )}
                </div>
              </td>
              <td className="text-right">
                <div className="table-actions">
                  <Link to={`/customers/${customer.id}`} className="btn-icon" title="View customer">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CustomerTable;
